import { useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Download, Eye, Loader2, Mail, Megaphone, MousePointerClick, Pencil, Plus, Share2, Trash2, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { MediaInput } from "./MediaInput";

const CATEGORIES = [
  ["financing", "Mortgage & Financing"], ["real_estate_agent", "Real Estate Agent"], ["inspections", "Home Inspection"],
  ["cleaning", "Cleaning Services"], ["moving", "Moving Companies"], ["junk_removal", "Junk Removal"],
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES);
const cardShadow = { boxShadow: "0 4px 20px rgba(15,23,42,0.035)" };
const EMPTY_FORM = { business_name: "", category: "financing", logo_url: "", profile_photo_url: "", cover_photo_url: "", headline: "", license_number: "", description: "", bio: "", gallery_photos: [], destination_url: "", button_label: "Visit Website", contact_email: "", contact_phone: "", service_area: "", disclosure_text: "", active_from: "", active_until: "", enabled: true };

export function AdvertisersTab() {
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [viewingInquiries, setViewingInquiries] = useState(null);
  const [sharing, setSharing] = useState(null);

  const reload = () => api.get("/admin/advertisers").then((r) => setAdvertisers(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { reload(); }, []);

  const remove = async (advertiser) => {
    if (!window.confirm(`Remove "${advertiser.business_name}"? This also removes it from every property website it's assigned to.`)) return;
    try { await api.delete(`/admin/advertisers/${advertiser.id}`); toast.success("Advertiser removed"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggleEnabled = async (advertiser, enabled) => {
    try { await api.patch(`/admin/advertisers/${advertiser.id}`, { ...advertiser, enabled }); toast.success(enabled ? "Advertiser enabled" : "Advertiser disabled"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl space-y-7 fade-up" data-testid="advertisers-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div><span className="eyebrow">Sponsorships</span><h1 className="font-serif-display text-4xl sm:text-[42px] mt-2 text-[#101828]">Advertisers</h1><p className="text-sm text-[#475467] mt-3 max-w-2xl">Local businesses you can feature on property websites — financing, inspections, moving, and more. Assign them to specific listings from each project's Property Website tab.</p></div>
        <Button data-testid="advertiser-add-btn" onClick={() => setAdding(true)} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white gap-2"><Plus size={16} />Add advertiser</Button>
      </header>

      <section className="rounded-[14px] border border-[#E4E9F0] bg-white overflow-hidden" style={cardShadow} data-testid="advertisers-list">
        {loading && <div className="p-10 text-center text-sm text-[#94A3B8]">Loading…</div>}
        {!loading && advertisers.length === 0 && <div data-testid="advertisers-empty" className="p-12 text-center text-sm text-[#94A3B8]"><Megaphone size={28} className="mx-auto mb-3 text-[#CBD5E1]" />No advertisers yet. Add your first local-service partner.</div>}
        {advertisers.map((ad) => (
          <div key={ad.id} data-testid={`advertiser-row-${ad.id}`} className="flex items-center gap-4 px-5 sm:px-6 py-4 border-b border-[#F1F2F4] last:border-b-0 hover:bg-[#F8FAFC]">
            {(ad.logo_url || ad.profile_photo_url) ? (
              <>
                <img src={ad.logo_url || ad.profile_photo_url} alt="" onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling.style.display = "grid"; }} className="h-10 w-10 rounded-lg object-contain border border-[#E4E9F0] shrink-0" />
                <div className="h-10 w-10 rounded-lg bg-[#F7F9FC] border border-[#E4E9F0] hidden place-items-center text-[#94A3B8] shrink-0"><Megaphone size={16} /></div>
              </>
            ) : <div className="h-10 w-10 rounded-lg bg-[#F7F9FC] border border-[#E4E9F0] grid place-items-center text-[#94A3B8] shrink-0"><Megaphone size={16} /></div>}
            <div className="min-w-0 flex-1">
              <div className="font-medium text-[#101828] truncate">{ad.business_name}</div>
              <div className="text-xs text-[#475467] font-mono mt-0.5">{CATEGORY_LABEL[ad.category] || ad.category}{ad.service_area ? ` · ${ad.service_area}` : ""}</div>
            </div>
            <div data-testid={`advertiser-stats-${ad.id}`} className="hidden sm:flex items-center gap-4 text-xs text-[#475467] font-mono shrink-0">
              <span className="flex items-center gap-1.5" title="Views on property websites"><Eye size={13} className="text-[#94A3B8]" />{ad.view_count || 0} <span className="text-[#94A3B8]">views</span></span>
              <span className="flex items-center gap-1.5" title="Visits to their own public profile page"><UserRound size={13} className="text-[#94A3B8]" />{ad.profile_view_count || 0} <span className="text-[#94A3B8]">profile views</span></span>
              <span className="flex items-center gap-1.5" title="Clicks on this advertiser's placement"><MousePointerClick size={13} className="text-[#94A3B8]" />{ad.click_count || 0} <span className="text-[#94A3B8]">clicks</span></span>
            </div>
            <Switch data-testid={`advertiser-enabled-${ad.id}`} checked={ad.enabled !== false} onCheckedChange={(checked) => toggleEnabled(ad, checked)} />
            <button data-testid={`advertiser-inquiries-${ad.id}`} onClick={() => setViewingInquiries(ad)} title="Inquiries" className="relative p-2 text-[#475467] hover:text-[#0C2347]">
              <Mail size={16} />
              {ad.unread_inquiries > 0 && <span data-testid={`advertiser-inquiries-badge-${ad.id}`} className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-semibold grid place-items-center leading-none">{ad.unread_inquiries}</span>}
            </button>
            <button data-testid={`advertiser-share-${ad.id}`} onClick={() => setSharing(ad)} title="Share profile" className="p-2 text-[#475467] hover:text-[#0C2347]"><Share2 size={16} /></button>
            <button data-testid={`advertiser-edit-${ad.id}`} onClick={() => setEditing(ad)} title="Edit" className="p-2 text-[#475467] hover:text-[#0C2347]"><Pencil size={16} /></button>
            <button data-testid={`advertiser-delete-${ad.id}`} onClick={() => remove(ad)} title="Remove" className="p-2 text-red-600 hover:text-red-700"><Trash2 size={16} /></button>
          </div>
        ))}
      </section>

      {(adding || editing) && (
        <AdvertiserFormDialog advertiser={editing} onClose={() => { setAdding(false); setEditing(null); }} onSaved={() => { setAdding(false); setEditing(null); reload(); }} />
      )}
      {viewingInquiries && (
        <AdvertiserInquiriesDialog advertiser={viewingInquiries} onClose={() => { setViewingInquiries(null); reload(); }} />
      )}
      {sharing && (
        <AdvertiserShareDialog advertiser={sharing} onClose={() => setSharing(null)} />
      )}
    </div>
  );
}

function AdvertiserFormDialog({ advertiser, onClose, onSaved }) {
  const [form, setForm] = useState(advertiser ? { ...EMPTY_FORM, ...advertiser } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.business_name.trim()) { toast.error("Enter a business name"); return; }
    setSaving(true);
    const payload = { ...form, contact_email: form.contact_email || null, active_from: form.active_from || null, active_until: form.active_until || null };
    try {
      if (advertiser) await api.patch(`/admin/advertisers/${advertiser.id}`, payload);
      else await api.post("/admin/advertisers", payload);
      toast.success(advertiser ? "Advertiser updated" : "Advertiser added");
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const field = (key, label, props = {}) => (
    <div>
      <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">{label}</Label>
      <Input data-testid={`advertiser-form-${key}`} value={form[key] || ""} onChange={(e) => set(key)(e.target.value)} className="mt-1 rounded-xl border-[#E4E9F0]" {...props} />
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="advertiser-form-dialog" className="rounded-xl max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl font-normal text-[#101828]">{advertiser ? "Edit advertiser" : "Add advertiser"}</DialogTitle>
          <DialogDescription>Shown as a sponsored placement on any property website you assign it to.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("business_name", "Business name")}
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">Category</Label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="advertiser-form-category" className="mt-1 rounded-xl border-[#E4E9F0]"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("headline", "Headline", { placeholder: "Low rates, fast pre-approval", maxLength: 140 })}
          {field("license_number", "License # (optional)", { placeholder: "e.g. TREC #0123456", maxLength: 40 })}
          {field("button_label", "Button label", { placeholder: "Visit Website" })}
          {field("destination_url", "Destination URL", { placeholder: "https://… (leave blank to collect inquiries instead)" })}
          {field("service_area", "Service area", { placeholder: "Greater Austin area" })}
          {field("contact_email", "Contact email", { type: "email" })}
          {field("contact_phone", "Contact phone")}
          {field("active_from", "Active from", { type: "date" })}
          {field("active_until", "Active until", { type: "date" })}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <MediaInput label="Logo" value={form.logo_url} onChange={set("logo_url")} testid="advertiser-form-logo_url" accept="image/*" />
          <MediaInput label="Profile photo (headshot)" value={form.profile_photo_url} onChange={set("profile_photo_url")} testid="advertiser-form-profile_photo_url" accept="image/*" />
          <MediaInput label="Cover photo" value={form.cover_photo_url} onChange={set("cover_photo_url")} testid="advertiser-form-cover_photo_url" accept="image/*" />
        </div>
        <div>
          <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">Description</Label>
          <p className="text-xs text-[#94A3B8] mt-0.5">Short blurb shown on cards and banners across property websites.</p>
          <Textarea data-testid="advertiser-form-description" rows={2} maxLength={600} value={form.description} onChange={(e) => set("description")(e.target.value)} className="mt-1.5 rounded-xl border-[#E4E9F0]" />
        </div>
        <div>
          <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">About me (optional)</Label>
          <p className="text-xs text-[#94A3B8] mt-0.5">A longer bio shown only on their public profile page — background, specialties, what sets them apart.</p>
          <Textarea data-testid="advertiser-form-bio" rows={5} maxLength={2000} placeholder="e.g. With 12 years serving the greater metro area, I specialize in..." value={form.bio} onChange={(e) => set("bio")(e.target.value)} className="mt-1.5 rounded-xl border-[#E4E9F0]" />
        </div>
        <GalleryPhotosInput value={form.gallery_photos} onChange={set("gallery_photos")} />
        <div>
          <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">Disclosure text (optional)</Label>
          <Input data-testid="advertiser-form-disclosure" placeholder="e.g. Paid advertisement, not an endorsement" value={form.disclosure_text} onChange={(e) => set("disclosure_text")(e.target.value)} className="mt-1 rounded-xl border-[#E4E9F0]" />
        </div>
        <div className="flex items-center justify-between rounded-xl border border-[#E4E9F0] bg-[#F7F9FC] px-4 py-3">
          <div><div className="text-sm font-medium text-[#101828]">Enabled</div><div className="text-xs text-[#475467]">Disabled advertisers are hidden everywhere, even if assigned.</div></div>
          <Switch data-testid="advertiser-form-enabled" checked={form.enabled} onCheckedChange={set("enabled")} />
        </div>
        <DialogFooter>
          <Button data-testid="advertiser-form-cancel" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="advertiser-form-save" disabled={saving} onClick={save} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white">{saving ? "Saving…" : "Save advertiser"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AdvertiserInquiriesDialog({ advertiser, onClose }) {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/advertisers/${advertiser.id}/inquiries`).then((r) => setInquiries(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.post(`/admin/advertisers/${advertiser.id}/inquiries/mark-read`).catch(() => {});
  }, [advertiser.id]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="advertiser-inquiries-dialog" className="rounded-xl max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-normal text-[#101828]">Inquiries — {advertiser.business_name}</DialogTitle>
          <DialogDescription>Messages sent from this advertiser's property-website placements and public profile page.</DialogDescription>
        </DialogHeader>
        {loading && <div className="p-6 text-center text-sm text-[#94A3B8]">Loading…</div>}
        {!loading && inquiries.length === 0 && <div data-testid="advertiser-inquiries-empty" className="p-6 text-center text-sm text-[#94A3B8]">No inquiries yet.</div>}
        <div className="space-y-3">
          {inquiries.map((inq) => (
            <div key={inq.id} data-testid={`advertiser-inquiry-${inq.id}`} className="rounded-lg border border-[#E4E9F0] p-3.5">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-[#101828]">{inq.name}</div>
                <div className="text-[11px] text-[#94A3B8] font-mono">{new Date(inq.created_at).toLocaleString()}</div>
              </div>
              <div className="text-xs text-[#475467] mt-0.5">{inq.email}{inq.phone ? ` · ${inq.phone}` : ""} · <span className="font-mono uppercase">{inq.source === "profile" ? "Profile page" : "Property website"}</span></div>
              {inq.message && <p className="text-sm text-[#101828] mt-2">{inq.message}</p>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button data-testid="advertiser-inquiries-close" variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AdvertiserShareDialog({ advertiser, onClose }) {
  const qrRef = useRef(null);
  const profileUrl = advertiser.slug ? `${window.location.origin}/local-experts/${advertiser.slug}` : "";

  const copy = async () => {
    try { await navigator.clipboard.writeText(profileUrl); toast.success("Profile link copied"); }
    catch { toast.error("Couldn't copy link"); }
  };
  const downloadQrCode = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${(advertiser.slug || "advertiser")}-qr-code.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="advertiser-share-dialog" className="rounded-xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-normal text-[#101828]">Share {advertiser.business_name}'s profile</DialogTitle>
          <DialogDescription>Hand this link or QR code to {advertiser.business_name} to share on social media, in emails, or printed at open houses.</DialogDescription>
        </DialogHeader>
        {profileUrl ? (
          <>
            <div className="flex items-center gap-2">
              <Input readOnly data-testid="advertiser-share-url" value={profileUrl} className="rounded-lg border-[#E4E9F0] font-mono text-xs" />
              <Button variant="outline" size="icon" data-testid="advertiser-share-copy" onClick={copy} className="rounded-lg border-[#E4E9F0] shrink-0"><Copy size={15} /></Button>
            </div>
            <div className="flex flex-col items-center gap-3 pt-2">
              <div ref={qrRef} className="p-3 rounded-xl border border-[#E4E9F0] bg-white"><QRCodeCanvas value={profileUrl} size={180} level="M" includeMargin /></div>
              <Button data-testid="advertiser-share-download-qr" variant="outline" onClick={downloadQrCode} className="rounded-lg border-[#E4E9F0] gap-2"><Download size={14} />Download QR code</Button>
            </div>
          </>
        ) : <p className="text-sm text-[#94A3B8]">This advertiser doesn't have a profile slug yet.</p>}
        <DialogFooter>
          <Button data-testid="advertiser-share-close" variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const GALLERY_MAX = 8;

function GalleryPhotosInput({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const photos = value || [];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= GALLERY_MAX) { toast.error(`You can add up to ${GALLERY_MAX} photos`); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...photos, r.data.url]);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const remove = (i) => onChange(photos.filter((_, idx) => idx !== i));

  return (
    <div>
      <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">Photo gallery (optional)</Label>
      <p className="text-xs text-[#94A3B8] mt-0.5">A few extra photos — past sales, listings, or the team — shown on their public profile page. Up to {GALLERY_MAX}.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {photos.map((url, i) => (
          <div key={`${url}-${i}`} data-testid={`advertiser-gallery-photo-${i}`} className="relative h-20 w-20 rounded-lg overflow-hidden border border-[#E4E9F0] group">
            <img src={url} alt="" className="h-full w-full object-cover" />
            <button type="button" data-testid={`advertiser-gallery-photo-remove-${i}`} onClick={() => remove(i)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition"><X size={12} /></button>
          </div>
        ))}
        {photos.length < GALLERY_MAX && (
          <button type="button" data-testid="advertiser-gallery-photo-add" disabled={busy} onClick={() => ref.current?.click()} className="h-20 w-20 rounded-lg border border-dashed border-[#CBD5E1] text-[#94A3B8] grid place-items-center hover:border-[#0C2347] hover:text-[#0C2347] transition">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={18} />}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
      <p className="text-[11px] text-[#94A3B8] mt-1">{photos.length}/{GALLERY_MAX} photos</p>
    </div>
  );
}
